import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { CurrencyCode, Wallet } from '../types';
import { walletService } from '../services/payment/walletService';
import { USD_RATES } from '../lib/currencies';

interface WalletContextValue {
  wallets: Wallet[];
  loading: boolean;
  refresh: () => void;
  create: (label: string, currency: CurrencyCode, type: 'fiat' | 'crypto') => Promise<void>;
  transfer: (fromId: string, toId: string, amount: number) => Promise<void>;
  totalUsd: number;
}

const WalletContext = createContext<WalletContextValue | undefined>(undefined);

export function WalletProvider({ children }: { children: ReactNode }) {
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    const res = await walletService.list();
    if (res.ok) setWallets(res.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const create = useCallback(async (label: string, currency: CurrencyCode, type: 'fiat' | 'crypto') => {
    await walletService.create(label, currency, type);
    await refresh();
  }, [refresh]);

  const transfer = useCallback(async (fromId: string, toId: string, amount: number) => {
    await walletService.transfer(fromId, toId, amount);
    await refresh();
  }, [refresh]);

  const totalUsd = useMemo(
    () => wallets.reduce((sum, w) => sum + w.balance / (USD_RATES[w.currency] || 1), 0),
    [wallets],
  );

  const value = useMemo<WalletContextValue>(
    () => ({ wallets, loading, refresh, create, transfer, totalUsd }),
    [wallets, loading, refresh, create, transfer, totalUsd],
  );

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}

export function useWalletContext() {
  const ctx = useContext(WalletContext);
  if (!ctx) throw new Error('useWalletContext must be used within a WalletProvider');
  return ctx;
}
